import { useEffect, useState } from 'react';
import { ErreurApi } from '../api/client';
import {
  useEnlevement, useMajAdresse, type AdresseSaisie, type EnvironnementDhl, type PaletteSaisie,
  type ResultatEnlevement,
} from '../api/hooks';
import type { CommandeListee } from '../api/types';
import { useToasts } from './Toasts';
import { Repousser } from './Repousser';

/**
 * Demande d'enlèvement pour les commandes stockées chez HIOLLE : DHL Freight
 * pour des palettes, DPD pour des colis.
 *
 * Le transporteur se déduit du mail de HIOLLE — « 2 colis » part chez DPD,
 * une palette chez DHL — et ne se choisit à l'écran que si le mail n'en dit
 * rien, ou qu'il n'y a pas de mail.
 *
 * **Deux temps, jamais un seul.** On compose d'abord l'instruction et on la
 * montre — palettes, poids, dates, code produit, avertissements —, et c'est
 * seulement ensuite que « Envoyer la demande » réserve. L'aperçu se compose
 * seul, à l'ouverture puis à chaque modification. Réserver fait venir un camion
 * chez HIOLLE et se facture : c'est une écriture chez un tiers, au même titre
 * qu'un mail qui part, et la maison ne les fait jamais sans relecture.
 *
 * **Chez DHL, un essai avant la production.** « Essai en bac à sable » envoie
 * la demande à DHL sans faire venir de camion, et rend les étiquettes, le
 * bordereau et la lettre de voiture, à ouvrir ici. « Réserver en production »
 * ne s'ouvre qu'ensuite, et seulement tant que la demande affichée est celle
 * de l'essai : l'API compare les deux et refuse au moindre écart.
 *
 * Les palettes viennent du mail de HIOLLE, collé ici, ou se saisissent à la
 * main. La lecture du mail n'est qu'un pré-remplissage : elle peut se tromper,
 * et c'est précisément pour cela qu'elle passe sous les yeux.
 */
export function EnlevementHiolle({
  commande, onFermer,
}: {
  commande: CommandeListee;
  onFermer: () => void;
}) {
  const { ajouter } = useToasts();
  const enlevement = useEnlevement();
  const majAdresse = useMajAdresse();

  const [texteMail, setTexteMail] = useState('');
  /** L'adresse de livraison en cours de correction ; `null` hors édition. */
  const [adresse, setAdresse] = useState<AdresseSaisie | null>(null);
  /**
   * Le refus de l'adresse, affiché sous son bouton. Dans l'encart d'erreur du
   * bas de la fenêtre, il passait inaperçu : « Enregistrer » semblait ne rien
   * faire.
   */
  const [erreurAdresse, setErreurAdresse] = useState<string | null>(null);
  const [palettes, setPalettes] = useState<PaletteSaisie[]>([]);
  /**
   * Ce que la lecture du mail a supposé. Gardé à part : dès la recomposition
   * suivante, les palettes sont des saisies, et l'API n'en dit plus rien.
   */
  const [doutesPalettes, setDoutesPalettes] = useState<string[]>([]);
  /** Les doutes ont été relus et acceptés : la case cochée sous l'encadré. */
  const [doutesVerifies, setDoutesVerifies] = useState(false);
  const [valeur, setValeur] = useState('');
  const [instruction, setInstruction] = useState('');
  /** Transporteur choisi à l'écran ; `null` tant qu'on suit le mail de HIOLLE. */
  const [choix, setChoix] = useState<'dhl' | 'dpd' | null>(null);
  const [apercu, setApercu] = useState<ResultatEnlevement | null>(null);
  /**
   * Les pièces du dernier essai DHL fait dans cette fenêtre. L'API ne les
   * conserve pas : sans elles, la production reste fermée.
   */
  const [pieces, setPieces] = useState<{ nom: string; contenu: string }[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  /*
   * La recherche part à l'ouverture, sans qu'on la demande.
   *
   * C'est le geste attendu : on vient chercher les palettes que HIOLLE a
   * annoncées. Faire cliquer « Composer » d'abord obligeait à demander une
   * chose pour en obtenir une autre, et la fenêtre s'ouvrait sur un formulaire
   * vide, sans dire qu'elle savait peut-être déjà le remplir.
   */
  useEffect(() => { void composer(); /* une seule fois, à l'ouverture */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commande.id]);

  const occupe = enlevement.isPending;

  /*
   * L'aperçu se recompose seul après chaque modification.
   *
   * Il y avait un bouton « Composer », à côté de celui qui réserve : deux
   * boutons pour un seul geste, et le premier ne faisait rien partir. Il ne
   * reste que « Envoyer la demande », actif seulement quand l'aperçu affiché
   * est celui de la saisie en cours — la relecture est garantie sans clic de
   * plus. Le délai laisse finir la frappe avant d'interroger l'API.
   */
  const [aRecomposer, setARecomposer] = useState(0);
  const invalider = () => { setApercu(null); setARecomposer((n) => n + 1); };
  useEffect(() => {
    if (!aRecomposer) return;
    const minuteur = setTimeout(() => void composer(), 600);
    return () => clearTimeout(minuteur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aRecomposer]);

  /** Compose sans rien réserver, et remonte les palettes lues dans le mail. */
  async function composer() {
    setErreur(null);
    try {
      const r = await enlevement.mutateAsync({
        id: commande.id,
        corps: {
          ...(palettes.length ? { palettes } : {}),
          ...(texteMail.trim() ? { texteMail: texteMail.trim() } : {}),
          ...(valeur.trim() ? { valeur: Number(valeur) } : {}),
          ...(instruction.trim() ? { instructionLivraison: instruction.trim() } : {}),
          ...(choix ? { transport: choix } : {}),
          reserver: false,
        },
      });
      setApercu(r);
      // Les palettes lues deviennent modifiables : à partir d'ici, c'est
      // l'écran qui fait foi, plus le mail.
      if (r.sourcePalettes === 'mail') {
        setPalettes(r.palettes);
        setDoutesPalettes(r.avertissementsPalettes ?? []);
        setDoutesVerifies(false);
      }
      // Le mail retrouvé s'affiche, qu'on en ait tiré des palettes ou non.
      // Quand on n'en a rien tiré, c'est lui qui permet de les saisir : le
      // cacher revenait à réclamer un colisage qu'on avait sous les yeux.
      if (r.mailHiolle.extrait && !texteMail.trim()) setTexteMail(r.mailHiolle.extrait);
    } catch (err) {
      setApercu(null);
      setErreur(err instanceof ErreurApi ? err.message : "La demande n'a pas pu être composée.");
    }
  }

  /**
   * Réserve. N'est atteignable qu'après un aperçu.
   *
   * Chez DHL, `bac-a-sable` est l'essai : la fenêtre reste ouverte sur la
   * saisie, les documents rendus à vérifier, et la production s'ouvre. Chez
   * DPD, l'environnement n'a pas de sens — c'est sa configuration qui décide.
   */
  async function reserver(environnement: EnvironnementDhl) {
    setErreur(null);
    try {
      const r = await enlevement.mutateAsync({
        id: commande.id,
        corps: {
          palettes: apercu?.palettes ?? palettes,
          // Le mail fixe le transporteur : l'API le relit pour le vérifier.
          ...(texteMail.trim() ? { texteMail: texteMail.trim() } : {}),
          ...(valeur.trim() ? { valeur: Number(valeur) } : {}),
          ...(instruction.trim() ? { instructionLivraison: instruction.trim() } : {}),
          // Le choix de l'écran, qui ne vaut que devant un mail muet.
          transport: apercu?.transport ?? choix ?? 'dhl',
          reserver: true,
          ...(apercu?.transport === 'dpd' ? {} : { environnement }),
        },
      });
      if (r.transport === 'dhl' && r.envoi?.environnement === 'bac-a-sable') {
        // L'essai ne clôt rien : on garde la saisie, l'aperçu porte désormais
        // l'essai conforme, et les pièces s'ouvrent pour être vérifiées.
        setApercu({ ...r, reserve: false, envoi: undefined });
        setPieces(r.envoi.pieces ?? []);
        ajouter(
          `Essai DHL pour ${commande.ref} (envoi ${r.envoi.id}) : vérifiez les ${r.envoi.documents} `
          + 'pièce(s), puis réservez en production.',
          'ok',
        );
        return;
      }
      setApercu(r);
      const chez = r.transport === 'dpd' ? 'DPD' : 'DHL';
      ajouter(
        r.simule
          ? `Essai ${chez} pour ${commande.ref} : aucun chauffeur ne viendra, la commande n’est pas modifiée.`
          : `Enlèvement ${chez} réservé pour ${commande.ref} (${r.transport === 'dpd' ? 'colis' : 'envoi'} ${r.envoi?.id}).`,
        'ok',
      );
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : "La réservation a échoué.");
    }
  }

  /**
   * Enregistre l'adresse corrigée SUR LA COMMANDE, puis recompose.
   *
   * Pas une adresse propre à cette demande : c'est la fiche qui fait foi, et
   * une adresse corrigée ici pour DHL resterait fausse partout ailleurs.
   * L'essai DHL déjà fait ne vaut plus — l'API le voit à l'écart d'adresse.
   */
  async function enregistrerAdresse() {
    if (!adresse) return;
    setErreurAdresse(null);
    try {
      await majAdresse.mutateAsync({ id: commande.id, adresse });
      setAdresse(null);
      await composer();
    } catch (err) {
      setErreurAdresse(err instanceof ErreurApi ? err.message : "L'adresse n'a pas pu être enregistrée.");
    }
  }

  const modifierPalette = (i: number, champ: keyof PaletteSaisie, valeurBrute: string) => {
    invalider();
    // Corriger un poids, c'est avoir tranché ce que le mail laissait ouvert.
    if (champ === 'poids') setDoutesPalettes([]);
    setPalettes((p) => p.map((ligne, j) => (
      j === i ? { ...ligne, [champ]: Number(valeurBrute) || 0 } : ligne
    )));
  };

  const dpd = (apercu?.transport ?? choix) === 'dpd';
  // Rien de DHL ne s'affiche pour des colis : ce n'est pas DHL qui viendra.
  const r = dpd ? null : (apercu?.instruction?.resume ?? null);
  const rDpd = dpd ? (apercu?.instructionDpd?.resume ?? null) : null;
  const etat = apercu ? (dpd ? apercu.etatDpd : apercu.etatDhl) : null;
  const unite = dpd ? 'colis' : 'palette';
  const reserve = apercu?.reserve === true;
  const pret = dpd ? apercu?.instructionDpd : apercu?.instruction;
  // Déjà demandé ailleurs : bloquant en production, simple avertissement en essai.
  const bloqueParAilleurs = Boolean(apercu?.dejaDemande && etat?.production);
  /** Un poids lu par supposition, pas encore vérifié : aucun bouton ne s'ouvre. */
  const doutesOuverts = doutesPalettes.length > 0 && !doutesVerifies;
  const raisonDoutes = 'Vérifiez le poids des palettes et cochez la case de l’encadré orange.';
  /** Déjà demandé depuis l'outil : plus rien ne se réserve, essai compris. */
  const verrouille = apercu?.verrouille ?? null;
  /** DHL : l'essai affiché vaut pour la demande affichée, la production est ouverte. */
  const essai = dpd ? null : (apercu?.essaiDhl ?? null);
  /** Les pièces de l'essai, visibles ici : sans elles, rien à vérifier avant la production. */
  const piecesVisibles = Boolean(essai?.conforme && pieces?.length);
  const productionOuverte = Boolean(
    !dpd && apercu?.etatDhl.production && essai?.conforme && pret && piecesVisibles,
  );

  /** Pourquoi l'adresse ne se corrige plus ; `null` tant qu'elle le peut. */
  const adresseFigee = reserve
    ? 'L’enlèvement vient d’être demandé : l’adresse est entre les mains du transporteur.'
    : (commande.adresseVerrouillee ?? null);

  /** Le mail de HIOLLE parle de palettes ou de colis : c'est lui qui décide. */
  const impose = apercu?.transportLu ?? null;

  /** Change de transporteur : l'aperçu ne vaut plus, il se recompose. */
  const choisir = (t: 'dhl' | 'dpd') => {
    if (impose || t === (apercu?.transport ?? choix)) return;
    setChoix(t);
    invalider();
  };

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label={`Enlèvement ${commande.ref}`}>
        <div className="mhead">
          <h2>Demande d&apos;enlèvement — {commande.ref}</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          {/* Ce que la fenêtre est en train de faire, dit avant tout le reste.
              Une recherche silencieuse ressemble à un formulaire vide. */}
          {occupe && !apercu && (
            <p className="indice" style={{ marginBottom: 10 }}>
              <span className="spin" /> Recherche du mail de HIOLLE pour {commande.ref}…
            </p>
          )}

          {/* Ce que la recherche a donné, TOUJOURS dit — y compris quand elle
              a réussi. Le taire laissait croire qu'on n'avait pas cherché. */}
          {apercu?.mailHiolle.cherche && (
            <div
              className="fieldset"
              style={apercu.mailHiolle.trouve && apercu.palettes.length
                ? { borderLeft: '4px solid var(--jaune)', background: 'var(--ocre-soft)' }
                : { borderLeft: '4px solid var(--brick)', background: 'var(--brick-soft)' }}
            >
              <h3 style={{ marginTop: 0 }}>
                {!apercu.mailHiolle.trouve
                  ? 'Mail de HIOLLE introuvable'
                  : apercu.palettes.length
                    ? `Mail de HIOLLE trouvé — ${apercu.palettes.length} ${dpd ? 'colis lu(s)' : 'palette(s) lue(s)'}`
                    : 'Mail de HIOLLE trouvé, mais aucune dimension lisible'}
              </h3>
              <p style={{ fontSize: 12.5, margin: '6px 0 0' }}>
                {!apercu.mailHiolle.trouve
                  ? <>{apercu.mailHiolle.raison} Saisissez le colisage ci-dessous, ou collez le
                      mail pour qu&apos;il en soit extrait.</>
                  : apercu.palettes.length
                    ? (dpd ? 'Relisez-les : ce sont eux que DPD viendra chercher.' : 'Relisez-les : ce sont elles qui partiront chez DHL.')
                    : <>Le message est repris ci-dessous. Saisissez les dimensions qu&apos;il
                        annonce — rien n&apos;a été deviné.</>}
              </p>
            </div>
          )}

          {/* Les deux adresses, toujours visibles : d'où part la marchandise et
              où elle va. Celle d'enlèvement ne dépend d'aucune donnée de la
              commande — c'est toujours l'entrepôt HIOLLE. */}
          {apercu && (
            <div className="fieldset">
              <h3 style={{ marginTop: 0 }}>Enlèvement</h3>
              <p style={{ fontSize: 13, margin: '0 0 10px' }}>
                <strong>{apercu.adresses.enlevement.nom}</strong><br />
                {apercu.adresses.enlevement.ligne}<br />
                {apercu.adresses.enlevement.codePostal} {apercu.adresses.enlevement.ville}
                {' · '}{apercu.adresses.enlevement.pays}
              </p>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                Livraison
                {/* Toujours là : grisé, avec sa raison, quand l'adresse ne se
                    corrige plus — jamais caché. */}
                {!adresse && (
                  <button
                    type="button" className="btn sm" disabled={occupe || Boolean(adresseFigee)}
                    title={adresseFigee ?? undefined}
                    onClick={() => {
                      const l = apercu.adresses.livraison;
                      setAdresse({
                        nom: l.nom === '—' ? '' : l.nom,
                        telephone: l.telephone ?? '',
                        ligne: l.ligne ?? '', complement: l.complement ?? '', codePostal: l.codePostal ?? '',
                        ville: l.ville ?? '', pays: l.pays ?? '',
                      });
                    }}
                  >
                    Modifier l&apos;adresse
                  </button>
                )}
              </h3>
              {adresseFigee && !adresse && (
                <p className="indice" style={{ margin: '0 0 6px' }}>{adresseFigee}</p>
              )}
              {adresse ? (
                <form onSubmit={(e) => { e.preventDefault(); void enregistrerAdresse(); }}>
                  <div className="rule">
                    <input
                      type="text" value={adresse.nom} placeholder="nom du destinataire"
                      aria-label="Nom du destinataire" maxLength={100} autoFocus
                      onChange={(e) => setAdresse({ ...adresse, nom: e.target.value })}
                    />
                    <input
                      type="text" inputMode="tel" value={adresse.telephone} placeholder="téléphone"
                      aria-label="Téléphone du destinataire" maxLength={30} style={{ flex: '0 0 150px' }}
                      onChange={(e) => setAdresse({ ...adresse, telephone: e.target.value })}
                    />
                  </div>
                  <div className="rule">
                    <input
                      type="text" value={adresse.ligne} placeholder="rue" aria-label="Rue"
                      maxLength={200}
                      onChange={(e) => setAdresse({ ...adresse, ligne: e.target.value })}
                    />
                  </div>
                  <div className="rule">
                    <input
                      type="text" value={adresse.complement} aria-label="Complément d’adresse"
                      placeholder="complément — bâtiment, étage, lieu-dit…" maxLength={100}
                      onChange={(e) => setAdresse({ ...adresse, complement: e.target.value })}
                    />
                  </div>
                  <div className="rule">
                    <input
                      type="text" value={adresse.codePostal} placeholder="code postal"
                      aria-label="Code postal" maxLength={20} style={{ flex: '0 0 110px' }}
                      onChange={(e) => setAdresse({ ...adresse, codePostal: e.target.value })}
                    />
                    <input
                      type="text" value={adresse.ville} placeholder="ville" aria-label="Ville"
                      maxLength={100}
                      onChange={(e) => setAdresse({ ...adresse, ville: e.target.value })}
                    />
                    <input
                      type="text" value={adresse.pays} placeholder="pays" aria-label="Pays"
                      maxLength={60} style={{ flex: '0 0 130px' }}
                      onChange={(e) => setAdresse({ ...adresse, pays: e.target.value })}
                    />
                  </div>
                  <div className="rule" style={{ marginBottom: 0 }}>
                    <button type="submit" className="btn sm pri" disabled={majAdresse.isPending || occupe}>
                      {majAdresse.isPending ? <><span className="spin" /> …</> : 'Enregistrer'}
                    </button>
                    <button
                      type="button" className="btn sm" disabled={majAdresse.isPending}
                      onClick={() => { setAdresse(null); setErreurAdresse(null); }}
                    >
                      Annuler
                    </button>
                  </div>
                  {erreurAdresse && (
                    <p className="err" role="alert" style={{ margin: '6px 0 0' }}>{erreurAdresse}</p>
                  )}
                  <p className="indice" style={{ marginBottom: 0 }}>
                    Enregistrée sur la commande : elle vaudra aussi pour le mail fournisseur et le suivi.
                    {!dpd && ' Un essai DHL déjà fait sera à refaire.'}
                  </p>
                </form>
              ) : (
                <p style={{ fontSize: 13, margin: 0 }}>
                  <strong>{apercu.adresses.livraison.nom}</strong><br />
                  {apercu.adresses.livraison.ligne
                    ?? <em style={{ color: 'var(--brick)' }}>rue manquante</em>}<br />
                  {apercu.adresses.livraison.complement && <>{apercu.adresses.livraison.complement}<br /></>}
                  {apercu.adresses.livraison.codePostal
                    ?? <em style={{ color: 'var(--brick)' }}>code postal manquant</em>}
                  {' '}
                  {apercu.adresses.livraison.ville
                    ?? <em style={{ color: 'var(--brick)' }}>ville manquante</em>}
                  {' · '}
                  {apercu.adresses.livraison.pays
                    ?? <em style={{ color: 'var(--brick)' }}>pays manquant</em>}
                  {apercu.adresses.livraison.telephone
                    ? <><br />{apercu.adresses.livraison.telephone}</>
                    : <><br /><em style={{ color: 'var(--ocre)' }}>pas de téléphone</em></>}
                </p>
              )}
            </div>
          )}

          {verrouille && !reserve && (
            <div
              className="fieldset"
              style={{ borderLeft: '4px solid var(--brick)', background: 'var(--brick-soft)' }}
            >
              <h3 style={{ margin: 0 }}>Enlèvement déjà demandé — réservation impossible</h3>
              <p style={{ fontSize: 12.5, margin: '6px 0 0' }}>{verrouille}</p>
            </div>
          )}

          {apercu?.dejaDemande && !reserve && (
            <div
              className="fieldset"
              style={{ borderLeft: '4px solid var(--brick)', background: 'var(--brick-soft)' }}
            >
              <h3 style={{ margin: 0 }}>
                {bloqueParAilleurs ? 'Enlèvement déjà demandé — réservation bloquée' : 'Enlèvement déjà demandé ?'}
              </h3>
              <p style={{ fontSize: 12.5, margin: '6px 0 0' }}>
                {apercu.dejaDemande}{' '}
                {bloqueParAilleurs
                  ? 'Réserver ferait venir un second chauffeur. Si cet enlèvement n’a pas eu lieu, décochez l’étape et retirez le tracking.'
                  : 'En essai, rien ne part : vous pouvez tester. En production, la réservation sera refusée.'}
              </p>
            </div>
          )}

          {/* Ce qui manque, nommé, et réparable là où il faut. */}
          {apercu && apercu.manques.length > 0 && (
            <div
              className="fieldset"
              style={{ borderLeft: '4px solid var(--jaune)', background: 'var(--ocre-soft)' }}
            >
              <h3 style={{ color: 'var(--ocre)', marginTop: 0 }}>
                Il manque {apercu.manques.length === 1 ? 'une chose' : `${apercu.manques.length} choses`}
              </h3>
              <ul style={{ fontSize: 12.5, color: 'var(--ocre)', margin: 0, paddingLeft: 18 }}>
                {apercu.manques.map((m) => <li key={m}>{m}</li>)}
              </ul>
              <p className="indice" style={{ marginBottom: 0, marginTop: 8 }}>
                Le colisage se saisit ci-dessous. Une adresse incomplète se corrige dans le bloc
                « Livraison » ci-dessus — elle est enregistrée sur la commande, qui fait foi.
              </p>
            </div>
          )}

          {/* Qui vient chercher : imposé par le mail s'il en parle, choisi ici sinon. */}
          {!reserve && (apercu || choix) && (
            <div className="field">
              <label>Transport</label>
              <div className="seg" role="group" aria-label="Transporteur">
                <button
                  type="button" aria-pressed={!dpd} disabled={Boolean(impose) && impose !== 'dhl'}
                  onClick={() => choisir('dhl')}
                >
                  Palettes — DHL Freight
                </button>
                <button
                  type="button" aria-pressed={dpd} disabled={Boolean(impose) && impose !== 'dpd'}
                  onClick={() => choisir('dpd')}
                >
                  Colis — DPD
                </button>
              </div>
              <p className="indice">
                {impose
                  ? `Imposé par le mail de HIOLLE, qui annonce ${impose === 'dpd' ? 'des colis' : 'des palettes'}.`
                  : 'Le mail de HIOLLE ne dit pas s’il s’agit de palettes ou de colis : choisissez.'}
              </p>
            </div>
          )}

          {/* L'état de l'intégration : savoir si l'on est en essai ou en
              production change la nature du geste. DHL a le sien, plus bas. */}
          {apercu && etat && dpd && (
            <div
              className="fieldset"
              style={etat.production && etat.disponible
                ? { borderLeft: '4px solid var(--brick)', background: 'var(--brick-soft)' }
                : { borderLeft: '4px solid var(--jaune)', background: 'var(--ocre-soft)' }}
            >
              <h3 style={{ margin: 0 }}>
                {!etat.disponible
                  ? `${dpd ? 'DPD' : 'DHL Freight'} non configuré`
                  : etat.production
                    ? `${dpd ? 'DPD' : 'DHL Freight'} en PRODUCTION`
                    : dpd ? 'DPD en environnement de test' : 'DHL Freight en bac à sable'}
              </h3>
              <p style={{ fontSize: 12.5, margin: '6px 0 0' }}>
                {etat.raison
                  ?? (dpd
                    ? 'Une demande fait passer un chauffeur DPD chez HIOLLE et se facture.'
                    : 'Une réservation fait venir un camion chez HIOLLE et se facture.')}
              </p>
            </div>
          )}

          {/* Les pièces de l'essai DHL, à ouvrir avant la production. La
              section n'existe que le temps de les vérifier. */}
          {!reserve && !verrouille && piecesVisibles && (
            <div className="fieldset">
              <h3 style={{ margin: 0 }}>Documents de l&apos;essai</h3>
              <p style={{ margin: '6px 0 0', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {pieces!.map((d) => (
                  <button key={d.nom} type="button" className="btn sm" onClick={() => ouvrirPdf(d)}>
                    {d.nom}
                  </button>
                ))}
              </p>
            </div>
          )}

          {!reserve && (
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="field">
                <label htmlFor="enl-mail">
                  {apercu?.mailHiolle.trouve ? 'Mail de HIOLLE trouvé' : 'Mail de HIOLLE'}
                </label>
                <textarea
                  id="enl-mail" rows={5} value={texteMail}
                  placeholder="Collez la réponse de HIOLLE si elle n’a pas été retrouvée."
                  onChange={(e) => { setTexteMail(e.target.value); invalider(); }}
                />
                <p className="indice">
                  À vide, l&apos;application cherche elle-même la réponse de HIOLLE pour cette
                  commande. Collez-la ici pour passer outre. Les dimensions sont lues telles
                  quelles — « 120 x 80 x 95 — 450 kg » —, et ce qui n&apos;est pas lu n&apos;est
                  pas inventé.
                </p>
              </div>

              {palettes.length > 0 && (
                <div className="field">
                  <label>{dpd ? 'Colis' : 'Palettes'} ({palettes.length})</label>
                  {palettes.map((p, i) => (
                    <div className="rule" key={i} style={{ marginTop: 4 }}>
                      {/* Chaque case nommée à côté : quatre nombres alignés se
                          lisaient mal, et la hauteur se confondait avec le poids. */}
                      {(['longueur', 'largeur', 'hauteur', 'poids'] as const).map((champ) => (
                        <label key={champ} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <input
                            type="number" min={1} value={p[champ] || ''}
                            aria-label={`${champ} ${unite} ${i + 1}`}
                            placeholder={champ === 'poids' ? 'kg' : 'cm'}
                            onChange={(e) => modifierPalette(i, champ, e.target.value)}
                          />
                          <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                            {{ longueur: 'long', largeur: 'larg', hauteur: 'haut', poids: 'kg' }[champ]}
                          </span>
                        </label>
                      ))}
                      <button
                        type="button" className="btn sm"
                        onClick={() => { invalider(); setPalettes((l) => l.filter((_, j) => j !== i)); }}
                      >
                        Retirer
                      </button>
                    </div>
                  ))}
                  {doutesPalettes.length > 0 && (
                    <div className="doute-bloc" role="alert">
                      {doutesPalettes.map((a) => <p key={a}><b>⚠ Poids :</b> {a}</p>)}
                      <label>
                        <input
                          type="checkbox" checked={doutesVerifies}
                          onChange={(e) => setDoutesVerifies(e.target.checked)}
                        />
                        J’ai vérifié le poids {dpd ? 'des colis' : 'des palettes'} ci-dessus
                      </label>
                    </div>
                  )}
                  {/* Les unités sont écrites à côté des cases : seule la limite DPD reste à dire. */}
                  {dpd && (
                    <p className="indice">
                      DPD ne reçoit que le nombre de colis et le poids, 31,5 kg au plus par colis.
                    </p>
                  )}
                </div>
              )}

              <div className="rule">
                <button
                  type="button" className="btn sm" disabled={occupe || palettes.length >= 7}
                  onClick={() => {
                    invalider();
                    setPalettes((p) => [...p, dpd
                      ? { longueur: 60, largeur: 40, hauteur: 0, poids: 0 }
                      : { longueur: 120, largeur: 80, hauteur: 0, poids: 0 }]);
                  }}
                >
                  {dpd ? 'Ajouter un colis' : 'Ajouter une palette'}
                </button>
                {/* DPD ne reçoit pas de valeur déclarée dans une demande d'enlèvement. */}
                {!dpd && (
                  <input
                    type="number" min={0} value={valeur} placeholder="valeur déclarée (€)"
                    aria-label="Valeur déclarée des marchandises"
                    onChange={(e) => { setValeur(e.target.value); invalider(); }}
                  />
                )}
              </div>

              <div className="field">
                <label htmlFor="enl-instr">Instruction de livraison</label>
                <input
                  id="enl-instr" value={instruction} maxLength={dpd ? 35 : 280}
                  placeholder="accès, étage, créneau…"
                  onChange={(e) => { setInstruction(e.target.value); invalider(); }}
                />
              </div>

            </form>
          )}

          {r && (
            <div className="fieldset" style={{ marginTop: 12 }}>
              <h3>Ce qui sera demandé</h3>
              <ul style={{ fontSize: 13, margin: 0, paddingLeft: 18 }}>
                <li>{r.palettes} palette(s) · {r.poidsTotal} kg · {r.volumeTotal} m³ · {r.metresPlancher} m de plancher</li>
                <li>
                  Enlèvement chez HIOLLE le <strong>{r.enlevement}</strong>, livraison
                  {r.gamme === 'standard' ? ' au plus tôt le ' : ' demandée le '}{r.livraison}
                  {r.gamme === 'standard' && ' (non garantie en Standard)'}
                </li>
                <li>
                  DHL Road Freight {r.gamme === 'standard' ? 'Standard' : 'Direct'} ({r.codeProduit})
                  {r.international ? ' — international' : ' — France'}
                </li>
                <li>Hayon au déchargement et prise de rendez-vous demandés</li>
              </ul>
              {(apercu?.instruction?.avertissements ?? []).map((a) => (
                <p key={a} className="indice" style={{ color: 'var(--ocre)', marginTop: 6 }}>{a}</p>
              ))}
            </div>
          )}

          {rDpd && (
            <div className="fieldset" style={{ marginTop: 12 }}>
              <h3>Ce qui sera demandé</h3>
              <ul style={{ fontSize: 13, margin: 0, paddingLeft: 18 }}>
                <li>{rDpd.colis} colis · {rDpd.poidsTotal} kg</li>
                <li>Passage du chauffeur DPD chez HIOLLE le <strong>{rDpd.enlevement}</strong></li>
                <li>Le chauffeur apporte les étiquettes : rien à imprimer chez HIOLLE</li>
              </ul>
              {(apercu?.instructionDpd?.avertissements ?? []).map((a) => (
                <p key={a} className="indice" style={{ color: 'var(--ocre)', marginTop: 6 }}>{a}</p>
              ))}
            </div>
          )}

          {apercu?.envoi && apercu.transport === 'dpd' && (
            <div
              className="fieldset"
              style={{ borderLeft: '4px solid var(--vert, #2e7d32)', marginTop: 12 }}
            >
              <h3>
                {apercu.simule
                  ? `Essai en environnement de test — aucun chauffeur, commande inchangée`
                  : 'Enlèvement demandé à DPD'}
              </h3>
              <ul style={{ fontSize: 13, margin: 0, paddingLeft: 18 }}>
                <li>Passage demandé le {apercu.envoi.jourEnlevement ?? '—'}</li>
                <li>Colis : {apercu.envoi.colis.join(', ')}</li>
                <li>
                  {apercu.envoi.brouillon.depose
                    ? 'Mail envoyé à contact@mosaik-floor.fr : à transférer à HIOLLE'
                    : `Mail non envoyé : ${apercu.envoi.brouillon.erreur ?? 'raison inconnue'}`}
                </li>
              </ul>
            </div>
          )}

          {apercu?.envoi && apercu.transport !== 'dpd' && (
            <div
              className="fieldset"
              style={{ borderLeft: '4px solid var(--vert, #2e7d32)', marginTop: 12 }}
            >
              <h3>
                {apercu.simule
                  ? `Essai en bac à sable — envoi ${apercu.envoi.id}, aucun camion, commande inchangée`
                  : `Réservé — envoi ${apercu.envoi.id}`}
              </h3>
              <ul style={{ fontSize: 13, margin: 0, paddingLeft: 18 }}>
                <li>Enlèvement retenu par DHL : {apercu.envoi.jourEnlevement ?? '—'}</li>
                {apercu.envoi.expedieLe && (
                  <li>Expédition inscrite au {apercu.envoi.expedieLe} — le client n’en est pas prévenu</li>
                )}
                <li>
                  Tracking : {apercu.envoi.tracking
                    ? <>{apercu.envoi.tracking}, inscrit sur la commande</>
                    : 'pas encore communiqué'}
                  {apercu.envoi.mailTracking && (apercu.envoi.mailTracking.etat === 'envoye'
                    ? ', envoyé au client'
                    : ` — mail au client NON parti : ${apercu.envoi.mailTracking.erreur ?? 'raison inconnue'}. Relancez-le depuis la fiche.`)}
                </li>
                <li>
                  {apercu.envoi.documents
                    ? `${apercu.envoi.documents} document(s)`
                    : 'Aucun document récupéré — à retirer sur le portail DHL'}
                  {apercu.envoi.brouillon.depose
                    ? `, envoyés à HIOLLE (${apercu.envoi.brouillon.destinataire ?? '—'})`
                      + (apercu.envoi.brouillon.enReponse ? ', en réponse à son mail' : '')
                    : ` — mail à HIOLLE NON envoyé : ${apercu.envoi.brouillon.erreur ?? 'raison inconnue'}. Transmettez les documents vous-même.`}
                </li>
              </ul>
            </div>
          )}

          {erreur && <p className="err" role="alert" style={{ marginTop: 10 }}>{erreur}</p>}
        </div>

        <div className="mfoot">
          <Repousser commande={commande} />
          <button type="button" className="btn" onClick={onFermer}>
            {reserve ? 'Fermer' : 'Annuler'}
          </button>
          {/* Le seul bouton qui engage. Actif seulement quand l'aperçu affiché
              est celui de la saisie en cours, et retiré une fois la demande
              faite : rien ne doit permettre de réserver deux fois le même
              enlèvement d'un double clic. Essai ou production, c'est l'encart
              d'état, au-dessus, qui le dit. */}
          {!reserve && !verrouille && dpd && (
            <button
              type="button" className="btn pri"
              disabled={occupe || !apercu || !etat?.disponible || !pret || bloqueParAilleurs || doutesOuverts}
              title={!apercu ? 'Aperçu en cours de mise à jour'
                : doutesOuverts ? raisonDoutes
                : !etat?.disponible ? (etat?.raison ?? undefined)
                : bloqueParAilleurs ? (apercu.dejaDemande ?? undefined) : undefined}
              onClick={() => void reserver('production')}
            >
              {occupe ? <><span className="spin" /> …</> : 'Envoyer la demande'}
            </button>
          )}
          {/* DHL : deux boutons, dans l'ordre où ils servent. Le second ne
              s'ouvre qu'avec un essai conforme à la saisie affichée — l'API
              le refuserait de toute façon. */}
          {!reserve && !verrouille && !dpd && (
            <>
              <button
                type="button" className="btn"
                disabled={occupe || !apercu || !apercu.etatDhl.disponible || !pret || doutesOuverts}
                title={!apercu ? 'Aperçu en cours de mise à jour'
                  : doutesOuverts ? raisonDoutes
                  : !apercu.etatDhl.disponible ? (apercu.etatDhl.raison ?? undefined) : undefined}
                onClick={() => void reserver('bac-a-sable')}
              >
                {occupe ? <><span className="spin" /> …</> : essai ? '1 · Refaire l’essai' : '1 · Essai en bac à sable'}
              </button>
              <button
                type="button" className="btn pri"
                disabled={occupe || !productionOuverte || bloqueParAilleurs || doutesOuverts}
                title={doutesOuverts ? raisonDoutes
                  : !apercu?.etatDhl.production ? (apercu?.etatDhl.raisonProduction ?? undefined)
                  : !essai ? 'Faites d’abord l’essai en bac à sable.'
                  : !essai.conforme ? `La demande a changé depuis l’essai : ${essai.ecarts.join(', ')}.`
                  : !pieces ? 'Les pièces de l’essai ne sont pas conservées : refaites l’essai pour les vérifier.'
                  : !pieces.length ? 'DHL n’a rendu aucune pièce à cet essai : refaites-le.'
                  : bloqueParAilleurs ? (apercu?.dejaDemande ?? undefined) : undefined}
                onClick={() => void reserver('production')}
              >
                2 · Réserver en production
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Ouvre un PDF rendu par DHL dans un nouvel onglet.
 *
 * Par un `Blob` et non une URL `data:` : les navigateurs refusent d'ouvrir ces
 * dernières en onglet, et les télécharger obligerait à les rouvrir.
 */
function ouvrirPdf(d: { nom: string; contenu: string }) {
  const octets = Uint8Array.from(atob(d.contenu), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([octets], { type: 'application/pdf' }));
  window.open(url, '_blank', 'noopener');
  // Laissé le temps de charger, puis rendu : l'onglet garde son document.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
